/**
 * Badge / Status Chip — Apex Design System v3.
 *
 * Uses semantic fill + text color pairs with a refined border treatment.
 * Optional leading dot indicator with glow pulse animation.
 * Pill radius, 11px/600 weight, warm tracking.
 */

import { clsx } from "clsx";

interface BadgeProps {
  children: React.ReactNode;
  variant?: "default" | "success" | "warning" | "danger" | "info";
  /** Show a leading dot indicator. */
  dot?: boolean;
  /** Animate the dot with a glow pulse. */
  glow?: boolean;
  className?: string;
}

const variantStyles = {
  default:
    "bg-[var(--surface-sunken)] text-[var(--text-secondary)] border-[var(--border-default)]",
  success:
    "bg-[var(--fill-success)] text-[var(--text-success)] border-[var(--text-success)]/20",
  warning:
    "bg-[var(--fill-warning)] text-[var(--text-warning)] border-[var(--text-warning)]/20",
  danger:
    "bg-[var(--fill-danger)] text-[var(--text-danger)] border-[var(--text-danger)]/20",
  info: "bg-[var(--fill-info)] text-[var(--text-info)] border-[var(--text-info)]/20",
};

const dotColors = {
  default: "bg-[var(--text-tertiary)]",
  success: "bg-[var(--text-success)]",
  warning: "bg-[var(--text-warning)]",
  danger: "bg-[var(--text-danger)]",
  info: "bg-[var(--text-info)]",
};

const glowClasses = {
  default: "glow-dot",
  success: "glow-dot glow-dot-success",
  warning: "glow-dot glow-dot-warning",
  danger: "glow-dot glow-dot-danger",
  info: "glow-dot",
};

function Badge({
  children,
  variant = "default",
  dot = false,
  glow = false,
  className,
}: BadgeProps) {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] px-2.5 py-0.5",
        "text-[11px] font-semibold tracking-wide leading-none",
        "border border-solid",
        variantStyles[variant],
        className,
      )}
    >
      {dot && (
        <span
          className={clsx(
            "h-1.5 w-1.5 rounded-full shrink-0",
            dotColors[variant],
            glow && glowClasses[variant],
          )}
        />
      )}
      {children}
    </span>
  );
}

export default Badge;
